import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AchievementsBadges.css';

const AchievementsBadges = ({ isOpen, onClose, inline = false }) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (isOpen || inline) {
      fetchAchievements();
    }
  }, [isOpen, inline]);

  const fetchAchievements = async () => {
    try {
      const response = await api.get('/api/gamification/achievements');
      setAchievements(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setLoading(false);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'LEGENDARY': return '#ff6b35';
      case 'EPIC': return '#9b59b6';
      case 'RARE': return '#3498db';
      case 'UNCOMMON': return '#2ecc71';
      default: return '#95a5a6';
    }
  };

  const getRarityGradient = (rarity) => {
    switch (rarity) {
      case 'LEGENDARY': 
        return 'linear-gradient(135deg, #ff6b35 0%, #f7971e 100%)';
      case 'EPIC': 
        return 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)';
      case 'RARE': 
        return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      case 'UNCOMMON': 
        return 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)';
      default: 
        return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
    }
  };

  const filteredAchievements = achievements.filter(achievement => {
    if (filter === 'all') return true;
    return achievement.category === filter;
  });

  const getCategories = () => {
    const categories = new Set(achievements.map(a => a.category));
    return ['all', ...Array.from(categories)];
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'problem_solving': '💡',
      'problems': '💡',
      'coding': '💻',
      'learning': '📚',
      'streak': '🔥',
      'practice': '🎯',
      'competition': '🏆',
      'milestone': '⭐',
      'special': '🌟',
      'code_quality': '✨'
    };
    return icons[category.toLowerCase()] || '🎖️';
  };

  const getCategoryDisplayName = (category) => {
    if (category === 'all') return 'All Categories';
    // Convert PROBLEM_SOLVING to "Problem Solving"
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (!isOpen && !inline) return null;

  const content = (
    <>
      {loading ? (
        <div className="loading">Loading achievements...</div>
      ) : (
        <>
          {/* Help Section */}
          {inline && (
            <div style={{ marginBottom: '20px', padding: '15px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: '12px', color: 'white' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>🏆 How to Earn Achievements</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6' }}>
                <li><strong>Solve Problems:</strong> Complete coding challenges to unlock badges</li>
                <li><strong>Bonus XP:</strong> Each achievement grants extra experience points</li>
                <li><strong>Rarity Levels:</strong> Common → Uncommon → Rare → Epic → Legendary</li>
                <li><strong>Track Progress:</strong> View locked achievements to see what you can earn next</li>
                <li><strong>Special Events:</strong> Unlock unique badges during coding competitions</li>
              </ul>
            </div>
          )}

          {/* Stats Cards - Redesigned */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }} title="Total number of achievements available">
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎯</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {achievements.length}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Total Available
              </div>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }} title="Achievements you've successfully unlocked">
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>✅</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {achievements.filter(a => a.unlockedAt).length}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Unlocked
              </div>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }} title="Achievements still waiting to be earned">
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔒</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {achievements.filter(a => !a.unlockedAt).length}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Locked
              </div>
            </div>
          </div>

          {/* Filter Tabs - Redesigned */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginBottom: '20px',
            flexWrap: 'wrap',
            background: 'white',
            padding: '12px',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {getCategories().map(category => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  background: filter === category 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#f3f4f6',
                  color: filter === category ? 'white' : '#374151',
                  boxShadow: filter === category 
                    ? '0 4px 6px rgba(102, 126, 234, 0.3)'
                    : 'none'
                }}
              >
                {category === 'all' ? '📋 All Categories' : `${getCategoryIcon(category)} ${getCategoryDisplayName(category)}`}
              </button>
            ))}
          </div>

          <div className="achievements-grid">
            {filteredAchievements.length === 0 ? (
              <div style={{ 
                gridColumn: '1 / -1', 
                textAlign: 'center', 
                padding: '40px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                color: 'white'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>🎯</div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>Start Your Achievement Journey!</h3>
                <p style={{ fontSize: '14px', opacity: 0.9 }}>
                  Solve coding problems in the Question Packages section to unlock your first achievement!
                </p>
              </div>
            ) : (
              filteredAchievements.map(achievement => (
                <div 
                  key={achievement.id}
                  className={`achievement-card ${achievement.unlockedAt ? 'unlocked' : 'locked'}`}
                  style={{
                    background: achievement.unlockedAt ? getRarityGradient(achievement.rarity) : '#f0f0f0',
                    borderColor: getRarityColor(achievement.rarity)
                  }}
                  title={achievement.unlockedAt ? `Unlocked on ${new Date(achievement.unlockedAt).toLocaleDateString()}` : `Complete the requirement to unlock this ${achievement.rarity.toLowerCase()} achievement`}
                >
                  <div className="achievement-icon">{achievement.icon}</div>
                  <div className="achievement-title">{achievement.name}</div>
                  <div className="achievement-description">{achievement.description}</div>
                  <div className="achievement-rarity">{achievement.rarity}</div>
                  {achievement.unlockedAt && (
                    <div className="achievement-date">
                      Unlocked: {new Date(achievement.unlockedAt).toLocaleDateString()}
                    </div>
                  )}
                  {achievement.bonusXp > 0 && (
                    <div className="achievement-bonus">+{achievement.bonusXp} XP</div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </>
  );

  // Inline version for dashboard
  if (inline) {
    return <div className="achievements-inline">{content}</div>;
  }

  // Modal version (original)
  return (
    <div className="achievements-modal-overlay" onClick={onClose}>
      <div className="achievements-modal" onClick={(e) => e.stopPropagation()}>
        <div className="achievements-header">
          <h2>🏆 Achievements & Badges</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {content}
      </div>
    </div>
  );
};

export default AchievementsBadges;
